import React, { useState, useEffect } from 'react';
import { fetchGalleryBySlug, getOptimizedImageUrl } from '../lib/cms';

export default function CmsGallery({ gallerySlug, customImages, title, description, columns = 4 }) {
  const [galleryData, setGalleryData] = useState(null);
  const [loading, setLoading] = useState(!customImages);
  const [activeImageIndex, setActiveImageIndex] = useState(null);

  useEffect(() => {
    if (customImages) {
      setGalleryData({ images: customImages, name: title, description });
      setLoading(false);
      return;
    }
    if (!gallerySlug) return;
    let mounted = true;
    fetchGalleryBySlug(gallerySlug).then((data) => {
      if (mounted) {
        setGalleryData(data);
        setLoading(false);
      }
    });
    return () => {
      mounted = false;
    };
  }, [gallerySlug, customImages, title, description]);

  // Key navigation for lightbox
  useEffect(() => {
    if (activeImageIndex === null) return;
    const handleKeyDown = (e) => {
      if (e.key === 'Escape') setActiveImageIndex(null);
      if (e.key === 'ArrowRight') handleNext();
      if (e.key === 'ArrowLeft') handlePrev();
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [activeImageIndex, galleryData]);

  const images = galleryData?.images || [];

  const handlePrev = (e) => {
    if (e) e.stopPropagation();
    if (!images.length) return;
    setActiveImageIndex((prev) => (prev > 0 ? prev - 1 : images.length - 1));
  };

  const handleNext = (e) => {
    if (e) e.stopPropagation();
    if (!images.length) return;
    setActiveImageIndex((prev) => (prev < images.length - 1 ? prev + 1 : 0));
  };

  if (loading) {
    return (
      <div className="py-8 grid grid-cols-2 md:grid-cols-4 gap-4 animate-pulse">
        {[1, 2, 3, 4].map((i) => (
          <div key={i} className="aspect-4/5 bg-[#EFEAE3] rounded-2xl" />
        ))}
      </div>
    );
  }

  if (!images || images.length === 0) {
    return null;
  }

  const gridColsClass =
    columns === 3
      ? 'grid-cols-1 sm:grid-cols-2 lg:grid-cols-3'
      : 'grid-cols-2 sm:grid-cols-2 lg:grid-cols-4';

  return (
    <div className="w-full">
      {(galleryData?.name || title) && (
        <div className="mb-6">
          <h3 className="text-xl md:text-2xl font-bold tracking-tight text-[#161514]">
            {title || galleryData.name}
          </h3>
          {(description || galleryData?.description) && (
            <p className="text-sm text-[#5C5852] mt-1">
              {description || galleryData.description}
            </p>
          )}
        </div>
      )}

      {/* Gallery Grid */}
      <div className={`grid ${gridColsClass} gap-3 md:gap-5`}>
        {images.map((imgUrl, idx) => (
          <button
            key={idx}
            type="button"
            onClick={() => setActiveImageIndex(idx)}
            className="group relative aspect-4/5 w-full overflow-hidden rounded-2xl bg-[#EAE4DC] text-left cursor-pointer border border-[#E8E2D9] transition-transform duration-300 focus:outline-none focus:ring-2 focus:ring-[#161514] focus:ring-offset-2"
          >
            <img
              src={getOptimizedImageUrl(imgUrl, { width: 700, quality: 80, format: 'auto' })}
              alt={`Galleri billede ${idx + 1}`}
              className="h-full w-full object-cover transition-transform duration-500 group-hover:scale-105"
              loading="lazy"
            />
            {/* Subtle Overlay Icon on Hover */}
            <div className="absolute inset-0 bg-black/25 opacity-0 group-hover:opacity-100 transition-opacity duration-300 flex items-center justify-center">
              <span className="w-10 h-10 rounded-full bg-white/90 text-[#161514] flex items-center justify-center text-sm shadow-md">
                <i className="fa-light fa-expand" aria-hidden="true" />
              </span>
            </div>
          </button>
        ))}
      </div>

      {/* Lightbox Modal */}
      {activeImageIndex !== null && images[activeImageIndex] && (
        <div
          role="dialog"
          aria-modal="true"
          aria-label="Galleri fuldskærm"
          className="fixed inset-0 z-50 flex items-center justify-center bg-black/90 p-4 md:p-8 backdrop-blur-sm"
          onClick={() => setActiveImageIndex(null)}
        >
          {/* Top bar controls */}
          <div className="absolute top-4 right-4 z-10 flex items-center gap-3">
            <span className="text-xs text-white/70 tracking-widest uppercase font-medium bg-white/10 px-3 py-1.5 rounded-full">
              {activeImageIndex + 1} / {images.length}
            </span>
            <button
              type="button"
              onClick={(e) => {
                e.stopPropagation();
                setActiveImageIndex(null);
              }}
              className="w-11 h-11 rounded-full bg-white/15 hover:bg-white/25 text-white flex items-center justify-center text-lg transition-colors cursor-pointer"
              aria-label="Luk visning"
            >
              <i className="fa-regular fa-xmark" aria-hidden="true" />
            </button>
          </div>

          {/* Navigation buttons */}
          {images.length > 1 && (
            <>
              <button
                type="button"
                onClick={handlePrev}
                className="absolute left-4 md:left-8 top-1/2 -translate-y-1/2 w-12 h-12 rounded-full bg-white/15 hover:bg-white/30 text-white flex items-center justify-center text-lg transition-colors cursor-pointer z-10"
                aria-label="Forrige billede"
              >
                <i className="fa-regular fa-arrow-left" aria-hidden="true" />
              </button>
              <button
                type="button"
                onClick={handleNext}
                className="absolute right-4 md:right-8 top-1/2 -translate-y-1/2 w-12 h-12 rounded-full bg-white/15 hover:bg-white/30 text-white flex items-center justify-center text-lg transition-colors cursor-pointer z-10"
                aria-label="Næste billede"
              >
                <i className="fa-regular fa-arrow-right" aria-hidden="true" />
              </button>
            </>
          )}

          {/* Main Image container */}
          <div
            className="max-h-[85vh] max-w-5xl overflow-hidden rounded-2xl shadow-2xl"
            onClick={(e) => e.stopPropagation()}
          >
            <img
              src={getOptimizedImageUrl(images[activeImageIndex], { width: 1800, quality: 90, format: 'auto' })}
              alt={`Galleri visning ${activeImageIndex + 1}`}
              className="max-h-[85vh] w-auto max-w-full object-contain mx-auto"
            />
          </div>
        </div>
      )}
    </div>
  );
}
